import React, {useEffect} from 'react';
import styled from 'styled-components';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import {FilterDrawerSearchableSelect} from '../../tickets/components/FilterDrawerSearchableSelect';
import {FilterDrawerDateRange} from '../../tickets/components/FilterDrawerDateRange';
import {raisedSurfaceShadow} from '../../../styles/elevation';
import {
	DASHBOARD_STATUS_OPTIONS,
	DASHBOARD_PRIORITY_OPTIONS,
	DASHBOARD_CHANNEL_OPTIONS,
	DASHBOARD_SLA_STATE_OPTIONS,
} from '../constants/dashboardFilterOptions';
import type {DashboardFilterState} from '../hooks/useDashboardFilterPanel';
import type {DashboardFilterOptions} from '../hooks/useDashboardFilterOptions';

interface Props {
	isOpen: boolean;
	draft: DashboardFilterState;
	filterOptions: DashboardFilterOptions;
	onClose: () => void;
	onApply: () => void;
	onUpdateDraft: (partial: Partial<DashboardFilterState>) => void;
	onClearDraft: () => void;
}

const Backdrop = styled.div`
	position: fixed;
	inset: 0;
	z-index: 40;
	background: rgb(0 0 0 / 0.3);
`;

const Panel = styled.aside`
	position: fixed;
	right: 0;
	top: 0;
	z-index: 50;
	display: flex;
	height: 100%;
	width: 320px;
	flex-direction: column;
	background: ${p => p.theme.sys.color.background.neutral['1']};
	${raisedSurfaceShadow}
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	border-bottom: ${p => p.theme.sys.size.borderwidth.thin} solid ${p => p.theme.sys.color.border.decorative};
	padding: 12px 16px;
`;

const Title = styled.h2`
	margin: 0;
	${p => p.theme.sys.font?.subheading?.['1']}
	color: ${p => p.theme.sys.color.text.content.main};
`;

// A dense icon trigger, not an action the design draws as a button — a local
// styled.button rather than the design-system Button (see sky-theme.md "Buttons").
const CloseButton = styled.button`
	border: 0;
	border-radius: ${p => p.theme.sys.size.borderradius['2']};
	background: transparent;
	padding: 4px;
	color: ${p => p.theme.sys.color.text.content.auxiliary};
	cursor: pointer;
	&:hover {
		background: ${p => p.theme.sys.color.background.disabled['1']};
		color: ${p => p.theme.sys.color.text.content.main};
	}
`;

const CloseIcon = styled.svg`
	height: 20px;
	width: 20px;
`;

const Body = styled.div`
	flex: 1 1 0%;
	overflow-y: auto;
	padding: 16px;
`;

const Group = styled.fieldset`
	border: 0;
	margin: 0 0 16px;
	padding: 0;
`;

const GroupLegend = styled.legend`
	display: block;
	width: 100%;
	padding: 0 0 8px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;

const Footer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	border-top: ${p => p.theme.sys.size.borderwidth.thin} solid ${p => p.theme.sys.color.border.decorative};
	padding: 12px 16px;
`;

export function DashboardFilterPanel({isOpen, draft, filterOptions, onClose, onApply, onUpdateDraft, onClearDraft}: Props) {
	useEffect(() => {
		if (!isOpen) return;
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key === 'Escape') onClose();
		}
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	return (
		<>
			<Backdrop onClick={onClose} aria-hidden="true" data-testid="dashboard-filter-backdrop" />

			<Panel role="dialog" aria-label="Filter panel">
				<Header>
					<Title>Filters</Title>
					<CloseButton type="button" onClick={onClose} aria-label="Close filters">
						<CloseIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
							<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
						</CloseIcon>
					</CloseButton>
				</Header>

				<Body>
					<Group>
						<GroupLegend>People</GroupLegend>
						<FilterDrawerSearchableSelect
							label="Assignee"
							options={filterOptions.assigneeOptions}
							selected={draft.assignee}
							onChange={assignee => onUpdateDraft({assignee})}
						/>
						<FilterDrawerSearchableSelect
							label="Requester"
							options={filterOptions.requesterOptions}
							selected={draft.requester}
							onChange={requester => onUpdateDraft({requester})}
						/>
					</Group>

					<Group>
						<GroupLegend>Ticket</GroupLegend>
						<FilterDrawerSearchableSelect
							label="Status"
							options={DASHBOARD_STATUS_OPTIONS}
							selected={draft.status}
							onChange={status => onUpdateDraft({status})}
						/>
						<FilterDrawerSearchableSelect
							label="Priority"
							options={DASHBOARD_PRIORITY_OPTIONS}
							selected={draft.priority}
							onChange={priority => onUpdateDraft({priority})}
						/>
						<FilterDrawerSearchableSelect
							label="Channel"
							options={DASHBOARD_CHANNEL_OPTIONS}
							selected={draft.channel}
							onChange={channel => onUpdateDraft({channel})}
						/>
						<FilterDrawerSearchableSelect
							label="Tags"
							options={filterOptions.labelOptions}
							selected={draft.tags}
							onChange={tags => onUpdateDraft({tags})}
						/>
					</Group>

					<Group>
						<GroupLegend>Linkage</GroupLegend>
						<FilterDrawerSearchableSelect
							label="SLA state"
							options={DASHBOARD_SLA_STATE_OPTIONS}
							selected={draft.slaState}
							onChange={slaState => onUpdateDraft({slaState})}
						/>
					</Group>

					<Group>
						<GroupLegend>Dates</GroupLegend>
						<FilterDrawerDateRange
							label="Last activity"
							from={draft.lastActivityRange.from}
							to={draft.lastActivityRange.to}
							onChangeFrom={from => onUpdateDraft({lastActivityRange: {...draft.lastActivityRange, from}})}
							onChangeTo={to => onUpdateDraft({lastActivityRange: {...draft.lastActivityRange, to}})}
						/>
						<FilterDrawerDateRange
							label="Resolved date"
							from={draft.resolvedDateRange.from}
							to={draft.resolvedDateRange.to}
							onChangeFrom={from => onUpdateDraft({resolvedDateRange: {...draft.resolvedDateRange, from}})}
							onChangeTo={to => onUpdateDraft({resolvedDateRange: {...draft.resolvedDateRange, to}})}
						/>
						<FilterDrawerDateRange
							label="First-response date"
							from={draft.firstResponseRange.from}
							to={draft.firstResponseRange.to}
							onChangeFrom={from => onUpdateDraft({firstResponseRange: {...draft.firstResponseRange, from}})}
							onChangeTo={to => onUpdateDraft({firstResponseRange: {...draft.firstResponseRange, to}})}
						/>
					</Group>
				</Body>

				{/* The panel's secondary and primary actions — design-system Button territory,
				    so it owns its own hover, focus ring and disabled treatment. */}
				<Footer>
					<Button emphasis={EMPHASIS.LOW} onPress={onClearDraft}>
						Clear all
					</Button>
					<Button emphasis={EMPHASIS.HIGH} onPress={onApply}>
						Apply
					</Button>
				</Footer>
			</Panel>
		</>
	);
}
